import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion, MotionConfig, MotionValue, useTransform } from "motion/react";
import React, { useEffect, useState } from "react";
import { useHeroScroll } from "../../context/HeroScrollContext";
import { LogoMark } from "../brand/LogoMark";

interface PageHeaderProps {
  logo?: React.ReactNode;
  /**
   * Optional override for nav styling.
   * If provided, this will be used instead of default static styles.
   * Useful for homepage transitions.
   */
  navStyles?: {
    backgroundColor?: string | MotionValue<string>;
    borderColor?: string | MotionValue<string>;
    textColor?: string | MotionValue<string>;
    hoverTextColor?: string | MotionValue<string>;
    hoverBgColor?: string | MotionValue<string>;
    activeBgColor?: string | MotionValue<string>;
    activeTextColor?: string | MotionValue<string>;
  };
  /**
   * If provided, the hamburger menu will be hidden (opacity 0, pointer-events-none)
   * until this motion value approaches 1.
   * Used on homepage to reveal nav only after scrolling past hero.
   */
  hideHamburgerUntil?: MotionValue<number>;
  /**
   * If true, the logo will be inline with the navigation.
   */
  inlineLogo?: boolean;
}

/**
 * Sticky header that animates into place with hero scroll progress.
 */
export function PageHeader({ logo, navStyles, hideHamburgerUntil, inlineLogo = false }: PageHeaderProps) {
  // Only use useHeroScroll if we're inside the provider - catch the error if not
  let headerOpacity: any = 1;
  let headerY: any = 0;
  
  try {
    const scroll = useHeroScroll();
    headerOpacity = scroll.headerOpacity;
    headerY = scroll.headerY;
  } catch (e) {
    // Not in HeroScrollProvider context - use static values (typical for non-homepage)
    // This is expected behavior for non-index pages
  }

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navItems = [
    { label: "Platform", href: "/platform" }, // Placeholder href
    { label: "Manifesto", href: "/manifesto" },
    { label: "Perspectives", href: "/perspectives" },
    { label: "Careers", href: "/careers" }, // Placeholder href
  ];

  const defaultStyles = {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderColor: "rgba(229, 229, 229, 0.5)", // neutral-200/50
    textColor: "rgb(82, 82, 82)", // neutral-600
    hoverTextColor: "rgb(0, 0, 0)",
    hoverBgColor: "rgb(245, 245, 245)", // neutral-100
    activeBgColor: "rgb(245, 245, 245)", // neutral-100
    activeTextColor: "rgb(0, 0, 0)",
  };

  const styles = { ...defaultStyles, ...navStyles };

  // Conditional hamburger visibility logic
  // Default to fully visible if no control prop is passed
  const hamburgerOpacity = hideHamburgerUntil 
    ? useTransform(hideHamburgerUntil, [0.8, 1], [0, 1]) 
    : 1;
    
  const hamburgerFilter = hideHamburgerUntil
    ? useTransform(hideHamburgerUntil, [0.8, 1], ["blur(8px)", "blur(0px)"])
    : "blur(0px)";

  const hamburgerPointerEvents = hideHamburgerUntil
      ? useTransform(hideHamburgerUntil, (v) => (v > 0.9 ? "auto" : "none"))
      : "auto";

  // Mobile-only background blur that fades in when scrolling past hero (when logo turns black)
  // hideHamburgerUntil is heroProgress [0..1]. 0 = black bg/white text, 1 = white bg/black text.
  // We want bg to appear when we transition to white (progress ~ 0.9 -> 1).
  const mobileBgOpacity = hideHamburgerUntil
      ? useTransform(hideHamburgerUntil, [0.85, 1], [0, 1])
      : 0;
  const mobileBgY = hideHamburgerUntil
      ? useTransform(hideHamburgerUntil, [0.85, 1], [-20, 0])
      : 0;

  // Calculate background blur opacity based on hideHamburgerUntil (hero scroll progress)
  // We want it to fade in as the background turns white (similar to mobileBgOpacity).
  // It's only visible on lg screens and up (via className), but we'll control opacity dynamically.
  // Ensure it's always a MotionValue to avoid type errors with useTransform
  // useTransform(value, output) where value is static 0 needs a dummy motion value if we want to keep types happy,
  // but actually standard pattern is to just use `useTransform` on a motion value we create.
  // Since we can't call hooks conditionally, we rely on the fact that we are already in a component.
  // However, we need a "zero" motion value if hideHamburgerUntil is undefined.
  const zeroMotionValue = useTransform(() => 0);
  const logoBgOpacity = hideHamburgerUntil
      ? useTransform(hideHamburgerUntil, [0.85, 1], [0, 1])
      : zeroMotionValue;

  return (
    <>
      {/* Mobile Header Background */}
      <motion.div 
          className="fixed top-0 left-0 right-0 h-[80px] bg-white/80 backdrop-blur-md z-50 md:hidden pointer-events-none"
          style={{ 
              opacity: mobileMenuOpen ? 0 : mobileBgOpacity,
              y: mobileMenuOpen ? 0 : mobileBgY,
          }}
      />

      <motion.header
        className={`fixed top-12 md:top-7 left-0 right-0 flex items-center justify-center pointer-events-none ${
          inlineLogo ? "justify-between" : ""
        }`}
        // If menu is open: z-[102] (above overlay), opacity 1 (visible), y 0 (reset position)
        // If menu is closed: z-60, dynamic opacity/y
        style={{
          opacity: mobileMenuOpen ? 1 : headerOpacity,
          y: mobileMenuOpen ? 0 : headerY,
          zIndex: mobileMenuOpen ? 102 : 60,
          top: undefined, // Remove explicit top overrides to prevent jumps
        }}
      >
        {logo && (
          <div className="absolute left-6 top-1/2 -translate-y-1/2 pointer-events-auto z-70 flex items-center justify-center rounded-full">
            <motion.div 
              className="absolute w-[250px] -inset-y-2 rounded-full -z-10 hidden md:block"
              style={{
                backgroundImage: useTransform(logoBgOpacity, (v: number) => 
                  v > 0.1 
                    ? `radial-gradient(closest-side, rgba(255, 255, 255, ${0.8 * v}) 0%, rgba(255, 255, 255, ${0.4 * v}) 50%, transparent 100%)`
                    : "none"
                ),
                // WebkitMaskImage: useTransform(logoBgOpacity, (v: number) => 
                //   v > 0.1 ? "radial-gradient(closest-side, black 50%, transparent 100%)" : "none"
                // ),
                // maskImage: useTransform(logoBgOpacity, (v: number) => 
                //   v > 1 ? "radial-gradient(closest-side, black 50%, transparent 100%)" : "none"
                // ),
                // boxShadow: useTransform(logoBgOpacity, (v: number) => v > 0.1 ? "0 1px 2px 0 rgba(0, 0, 0, 0.05)" : "none"),
                // backdropFilter: useTransform(logoBgOpacity, (v: number) => v > 1 ? `blur(${8 * v}px)` : "none"),
              }}
            />
            <motion.div
              className="flex items-center justify-center"
              style={{
                // When menu is open, force white color by filter
                filter: mobileMenuOpen ? "invert(1) brightness(2)" : "none",
                // Or if logo is text/svg that inherits color:
                color: mobileMenuOpen ? "white" : undefined,
              }}
            >
              {logo}
            </motion.div>
          </div>
        )}

        {/* Desktop Navigation */}
        <nav className="pointer-events-auto px-1 hidden md:block md:z-[1000]">
          <motion.ul
            className={`flex items-center gap-1 p-1 backdrop-blur-md border rounded-full shadow-sm ${
              inlineLogo ? "px-6" : ""
            }`}
            style={{
              backgroundColor: styles.backgroundColor as any,
              borderColor: styles.borderColor as any,
            }}
          >
            {inlineLogo && (
              <>
                <li className="pointer-events-auto z-70 flex items-center justify-center">
                  <LogoMark height={20} />
                </li>
                <li>
                  <Link
                    to="/"
                    className="block relative px-3 py-1.5 text-[13px] font-medium rounded-full transition-colors duration-200"
                    style={{
                      color: styles.textColor as any,
                    }}
                  >
                    <span>Home</span>
                  </Link>
                </li>
              </>
            )}
            {navItems.map((item) => (
              <li key={item.label}>
                <Link
                  to={item.href}
                  className="block relative px-3 py-1.5 text-[13px] font-medium rounded-full transition-colors duration-200"
                  activeProps={{
                    style: {
                      backgroundColor: "rgba(245, 245, 245, 0.1)",
                    },
                  }}
                >
                  {(props) => (
                    <motion.span
                      style={{
                        color: props.isActive
                          ? (styles.activeTextColor as any)
                          : (styles.textColor as any),
                      }}
                    >
                      {item.label}
                    </motion.span>
                  )}
                </Link>
              </li>
            ))}
          </motion.ul>
        </nav>

        {/* Mobile Hamburger Button */}
        <motion.div
          className="absolute right-6 top--3 md:top-0 md:hidden z-[101]"
          style={{
            opacity: mobileMenuOpen ? 1 : hamburgerOpacity,
            filter: mobileMenuOpen ? "blur(0px)" : hamburgerFilter,
            pointerEvents: mobileMenuOpen
              ? "auto"
              : (hamburgerPointerEvents as any),
          }}
        >
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="relative w-10 h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm pointer-events-auto"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <MotionConfig transition={{ duration: 0.3, ease: "easeInOut" }}>
              <motion.div className="relative w-5 h-4 flex flex-col justify-between">
                {/* Top Line */}
                <motion.span
                  className="w-full h-0.5 rounded-full origin-center bg-current"
                  style={{
                    backgroundColor: mobileMenuOpen
                      ? "#fff"
                      : (styles.textColor as any),
                  }}
                  animate={
                    mobileMenuOpen
                      ? { rotate: 45, y: 7 }
                      : { rotate: 0, y: 0 }
                  }
                />
                {/* Bottom Line */}
                <motion.span
                  className="w-full h-0.5 rounded-full origin-center bg-current"
                  style={{
                    backgroundColor: mobileMenuOpen
                      ? "#fff"
                      : (styles.textColor as any),
                  }}
                  animate={
                    mobileMenuOpen
                      ? { rotate: -45, y: -7 }
                      : { rotate: 0, y: 0 }
                  }
                />
              </motion.div>
            </MotionConfig>
          </button>
        </motion.div>
      </motion.header>

      {/* Mobile Fullscreen Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black text-white flex flex-col justify-center px-8 md:hidden pointer-events-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <nav className="flex flex-col gap-6 text-3xl font-sans tracking-wide">
              {[{ label: "Home", href: "/" }, ...navItems].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{
                    delay: 0.1 + i * 0.05,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                >
                  <Link
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block hover:text-neutral-400 transition-colors"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
